import { ORG_ID_LENGTH } from 'states/profile/business-rules';

export function getTechFee(organization) {
    if (!organization) {
        return 0;
    }

    return organization.tech_fee || 0;
}

/**
 * Normalizes a display name for sort and typeahead search.
 *
 * Sort and filter share this function so ordering and keystroke matching stay aligned
 * (e.g. &CO sorts under E and matches query "eco").
 *
 * Rules applied in order:
 * 1. Replace each `&` with `e` (product rule for org names like &CO)
 * 2. Lowercase
 * 3. Strip accents (NFD decomposition)
 *
 * @param {string} str - Display name (org label, user name, option label)
 * @returns {string} Normalized string for comparison
 */
function normalizeDisplayName(str) {
    if (!str) {
        return '';
    }
    return str
        .replace(/&/g, 'e')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');
}

/**
 * Moves organizations whose ids appear in pinToTopIds to the front, preserving pin order.
 * @param {Array} organizations
 * @param {string[]} pinToTopIds - Org ids to pin first (default none)
 * @param {string} idField - Field used for id matching (default: 'id')
 * @returns {Array}
 */
function pinOrganizationsToTop(organizations, pinToTopIds = [], idField = 'id') {
    if (!organizations?.length || !pinToTopIds?.length) {
        return organizations || [];
    }

    const remaining = [...organizations];
    const pinned = [];

    pinToTopIds.forEach(pinId => {
        const index = remaining.findIndex(org => org[idField] === pinId);
        if (index >= 0) {
            pinned.push(remaining[index]);
            remaining.splice(index, 1);
        }
    });

    return [...pinned, ...remaining];
}

/**
 * Resolves name/id field names from options or the shape of the first record.
 * MultiSelect options use label/value; API orgs typically use name/id.
 * @param {Array} organizations
 * @param {Object} options
 * @returns {{ nameField: string, idField: string }}
 */
function resolveOrganizationFields(organizations, options = {}) {
    const hasExplicitNameField =
        typeof options.nameField === 'string' && options.nameField.trim() !== '';
    const hasExplicitIdField = typeof options.idField === 'string' && options.idField.trim() !== '';

    if (hasExplicitNameField && hasExplicitIdField) {
        return { nameField: options.nameField, idField: options.idField };
    }

    const sample = organizations[0];
    if (
        sample &&
        Object.prototype.hasOwnProperty.call(sample, 'label') &&
        Object.prototype.hasOwnProperty.call(sample, 'value')
    ) {
        return { nameField: 'label', idField: 'value' };
    }

    return { nameField: 'name', idField: 'id' };
}

/**
 * Sorts records by display name using locale-aware comparison.
 * Works for organizations, users, or MultiSelect options (name/id or label/value)
 * via configurable field names.
 *
 * @remarks Display names are compared via {@link normalizeDisplayName} so sort order
 * matches typeahead search (accent/case insensitive; `&` treated as `e`).
 * @param {Array} records - Array of records to sort
 * @param {Object} options - Configuration options
 * @param {string} options.nameField - Field name for display name (inferred when omitted)
 * @param {string} options.idField - Field name for record id when pinning (inferred when omitted)
 * @param {string[]} options.pinToTopIds - Record ids to place at the top after sorting (default: [])
 * @returns {Array} - Sorted array (same shape as input)
 */
export function sortRecordsByName(records, options = {}) {
    if (!records || !Array.isArray(records)) {
        return [];
    }

    const { nameField, idField } = resolveOrganizationFields(records, options);
    const { pinToTopIds = [] } = options;

    const sorted = [...records].sort((a, b) => {
        // Same normalizer as filterRecordsByKeywords — & names group under E and match the same queries.
        const nameA = normalizeDisplayName(a[nameField]);
        const nameB = normalizeDisplayName(b[nameField]);
        return nameA.localeCompare(nameB, 'en', { sensitivity: 'base' });
    });
    return pinOrganizationsToTop(sorted, pinToTopIds, idField);
}

/**
 * True when keywords contain @ — gates email-field substring search.
 * @param {string} keywords
 * @returns {boolean}
 */
export function isEmailFragmentSearchInput(keywords) {
    if (!keywords || typeof keywords !== 'string') {
        return false;
    }

    return keywords.trim().includes('@');
}

/**
 * True when keywords exactly match the email local part or domain part (case-insensitive).
 * @param {string} email
 * @param {string} keywords - trimmed, no @
 * @returns {boolean}
 */
function matchesEmailLocalOrDomainPart(email, keywords) {
    if (!email || !keywords) {
        return false;
    }

    const atIndex = email.indexOf('@');
    if (atIndex <= 0) {
        return false;
    }

    const lower = keywords.toLowerCase();
    return (
        email.slice(0, atIndex).toLowerCase() === lower ||
        email.slice(atIndex + 1).toLowerCase() === lower
    );
}

/**
 * Filters records by search keywords.
 * Works for organizations, users, or MultiSelect options via configurable field names.
 * Matches against the name field and optionally the id field.
 *
 * @remarks Display-name matching uses {@link normalizeDisplayName} (same as sortRecordsByName).
 * Email and org-id branches use separate logic and do not apply the `&` → `e` rule.
 *
 * When emailField is set: input with @ uses case-insensitive substring on full email
 * (toLowerCase); input without @ matches exact local or domain part (toLowerCase).
 * @param {Array} records - Array of records to filter
 * @param {string} keywords - Search keywords
 * @param {Object} options - Configuration options
 * @param {string} options.nameField - Field name for display name (default: 'name')
 * @param {string} options.idField - Field name for record id (default: 'id')
 * @param {string} [options.emailField] - When set and input contains @, case-insensitive
 *   substring via toLowerCase; when input has no @, exact local or domain part via toLowerCase (OR with name/id).
 * @returns {Array} - Filtered array of records
 */
export function filterRecordsByKeywords(records, keywords, options = {}) {
    if (!records || !Array.isArray(records)) {
        return [];
    }

    const trimmed = (keywords || '').trim();

    // If no keywords provided, return all records
    if (!trimmed) {
        return records;
    }

    const { nameField = 'name', idField = 'id', emailField } = options;

    // Case-insensitive email substring (fragments + full email)
    if (emailField && isEmailFragmentSearchInput(trimmed)) {
        const lower = trimmed.toLowerCase();
        return records.filter(item => (item[emailField] || '').toLowerCase().includes(lower));
    }

    // Display names: same normalizer as sortRecordsByName (not used for email or org-id match).
    const normalizedKeywords = normalizeDisplayName(trimmed);

    return records.filter(record => {
        if (trimmed.length === ORG_ID_LENGTH) {
            return trimmed === record[idField];
        }

        const nameMatch = normalizeDisplayName(record[nameField]).indexOf(normalizedKeywords) >= 0;
        const emailPartMatch =
            emailField && matchesEmailLocalOrDomainPart(record[emailField], trimmed);
        return nameMatch || emailPartMatch;
    });
}
